import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { makeAuthenticatedRequest } from '../services/api';
import { FaArrowLeft, FaSpinner, FaExclamationCircle } from 'react-icons/fa';

const XeroContactDetailsPage = () => {
  const { contactId } = useParams();
  const [contactDetails, setContactDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContactDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const apiResponse = await makeAuthenticatedRequest('/xero/contacts/details', {
          method: 'POST',
          body: { contactId },
        });

        if (apiResponse && apiResponse.ContactID) {
          setContactDetails(apiResponse);
        } else if (apiResponse && apiResponse.message) {
          console.error('API returned a message but no contact data:', apiResponse.message);
          throw new Error(apiResponse.message || 'Failed to fetch contact details: API returned an error message.');
        } else {
          console.error('Error: Unexpected response structure from /xero/contacts/details API:', apiResponse);
          throw new Error('Failed to fetch contact details: Unexpected data format received.');
        }
      } catch (err) {
        console.error('Error fetching Xero contact details:', err);
        setError(err.message || 'An unknown error occurred.');
      } finally {
        setIsLoading(false);
      }
    };

    if (contactId) {
      fetchContactDetails();
    }
  }, [contactId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FaSpinner className="animate-spin text-4xl text-jims-cyan" />
        <p className="ml-3 text-lg text-gray-700">Loading contact details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <FaExclamationCircle className="text-5xl text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-red-700 mb-3">Error</h1>
        <p className="text-gray-600 mb-6">{error}</p>
        <Link 
          to="/settings/xero-import-contacts"
          className="btn btn-secondary inline-flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Import Contacts
        </Link>
      </div>
    );
  }

  if (!contactDetails) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-700">No Contact Details Found</h1>
        <p className="text-gray-600 mb-6">The requested contact details could not be found.</p>
        <Link 
          to="/settings/xero-import-contacts"
          className="btn btn-secondary inline-flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Import Contacts
        </Link>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const match = dateString.match(/\/Date\((\d+)([+-]\d+)?\)\//);
    if (match) {
      const timestamp = parseInt(match[1], 10);
      return new Date(timestamp).toLocaleString('en-AU');
    }
    return dateString;
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const parts = [
      address.AddressLine1,
      address.AddressLine2,
      address.AddressLine3,
      address.AddressLine4,
      address.City,
      address.Region,
      address.PostalCode,
      address.Country,
    ].filter(part => part && part.trim() !== '');
    return parts.join(', ') || 'N/A';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl font-bold text-jims-cyan-dark">
          Xero Contact: {contactDetails.Name}
        </h1>
        <Link
          to="/settings/xero-import-contacts"
          className="text-jims-cyan-dark hover:text-jims-cyan underline flex items-center text-sm"
        >
          <FaArrowLeft className="mr-1" /> Back to Import List
        </Link>
      </div>

      <div className="bg-white shadow-xl rounded-lg p-5 md:p-7 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <div><strong className="text-gray-500">Contact ID:</strong> <span className="text-gray-700">{contactDetails.ContactID}</span></div>
          {contactDetails.AccountNumber && <div><strong className="text-gray-500">Account Number:</strong> <span className="text-gray-700">{contactDetails.AccountNumber}</span></div>}
          <div><strong className="text-gray-500">Name:</strong> <span className="text-gray-700">{contactDetails.Name}</span></div>
          {contactDetails.FirstName && <div><strong className="text-gray-500">First Name:</strong> <span className="text-gray-700">{contactDetails.FirstName}</span></div>}
          {contactDetails.LastName && <div><strong className="text-gray-500">Last Name:</strong> <span className="text-gray-700">{contactDetails.LastName}</span></div>}
          <div><strong className="text-gray-500">Email:</strong> <span className="text-gray-700">{contactDetails.EmailAddress || 'N/A'}</span></div>
          <div><strong className="text-gray-500">Status:</strong> <span className="text-gray-700">{contactDetails.ContactStatus}</span></div>
          <div><strong className="text-gray-500">Is Customer:</strong> <span className="text-gray-700">{contactDetails.IsCustomer ? 'Yes' : 'No'}</span></div>
          <div><strong className="text-gray-500">Is Supplier:</strong> <span className="text-gray-700">{contactDetails.IsSupplier ? 'Yes' : 'No'}</span></div>
          {contactDetails.BankAccountDetails && (
            <>
              <div>
                <strong className="text-gray-500">BSB Code:</strong>
                <span className="text-gray-700">{contactDetails.BankAccountDetails.substring(0, 7)}</span>
              </div>
              <div>
                <strong className="text-gray-500">Bank Account Number:</strong>
                <span className="text-gray-700">{contactDetails.BankAccountDetails.substring(7)}</span>
              </div>
            </>
          )}
          {contactDetails.TaxNumber && <div><strong className="text-gray-500">ABN:</strong> <span className="text-gray-700">{contactDetails.TaxNumber}</span></div>}
          <div><strong className="text-gray-500">Updated Date (UTC):</strong> <span className="text-gray-700">{formatDate(contactDetails.UpdatedDateUTC)}</span></div>
          {contactDetails.ContactNumber && <div><strong className="text-gray-500">Contact Number:</strong> <span className="text-gray-700">{contactDetails.ContactNumber}</span></div>}
          <div><strong className="text-gray-500">Has Attachments:</strong> <span className="text-gray-700">{contactDetails.HasAttachments ? 'Yes' : 'No'}</span></div>
          <div><strong className="text-gray-500">Has Validation Errors:</strong> <span className="text-gray-700">{contactDetails.HasValidationErrors ? 'Yes' : 'No'}</span></div>
        </div>

        {contactDetails.Phones && contactDetails.Phones.length > 0 && (
          <div className="mt-5 pt-5 border-t">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Phones</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {contactDetails.Phones.map((phone, index) => (
                <div key={index} className="text-gray-700">
                  <strong className="text-gray-500">{phone.PhoneType}:</strong> {phone.PhoneNumber || 'N/A'} {phone.PhoneAreaCode && `(${phone.PhoneAreaCode})`} {phone.PhoneCountryCode && `+${phone.PhoneCountryCode}`}
                </div>
              ))}
            </div>
          </div>
        )}

        {contactDetails.Addresses && contactDetails.Addresses.length > 0 && (
          <div className="mt-5 pt-5 border-t">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Addresses</h3>
            {contactDetails.Addresses.map((address, index) => (
              <div key={index} className="mb-2 pb-2 border-b last:border-b-0 text-sm">
                <p className="text-gray-700">
                  <strong className="text-gray-500">{address.AddressType}:</strong> {formatAddress(address)}
                </p>
              </div>
            ))}
          </div>
        )}

        {contactDetails.Balances && (
            <div className="mt-5 pt-5 border-t">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Balances</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                        <h4 className="text-base font-medium text-gray-600">Accounts Payable</h4>
                        <p className="text-gray-700">Outstanding: {contactDetails.Balances.AccountsPayable?.Outstanding ?? 'N/A'}</p>
                        <p className="text-gray-700">Overdue: {contactDetails.Balances.AccountsPayable?.Overdue ?? 'N/A'}</p>
                    </div>
                    <div>
                        <h4 className="text-base font-medium text-gray-600">Accounts Receivable</h4>
                        <p className="text-gray-700">Outstanding: {contactDetails.Balances.AccountsReceivable?.Outstanding ?? 'N/A'}</p>
                        <p className="text-gray-700">Overdue: {contactDetails.Balances.AccountsReceivable?.Overdue ?? 'N/A'}</p>
                    </div>
                </div>
            </div>
        )}

        {contactDetails.BatchPayments && (
          <div className="mt-5 pt-5 border-t">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Batch Payments</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {contactDetails.BatchPayments.BankAccountName && <div><strong className="text-gray-500">Bank Account Name:</strong> <span className="text-gray-700">{contactDetails.BatchPayments.BankAccountName}</span></div>}
              {contactDetails.BatchPayments.BankAccountNumber && (
                <>
                  <div>
                    <strong className="text-gray-500">BSB Code: </strong>
                    <span className="text-gray-700">{contactDetails.BatchPayments.BankAccountNumber.substring(0, 7)}</span>
                  </div>
                  <div>
                    <strong className="text-gray-500">Bank Account Number: </strong>
                    <span className="text-gray-700">{contactDetails.BatchPayments.BankAccountNumber.substring(7)}</span>
                  </div>
                </>
              )}
              {contactDetails.BatchPayments.Details && <div><strong className="text-gray-500">Details:</strong> <span className="text-gray-700">{contactDetails.BatchPayments.Details}</span></div>}
            </div>
          </div>
        )}

        {contactDetails.ContactGroups && contactDetails.ContactGroups.length > 0 && (
          <div className="mt-5 pt-5 border-t">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Contact Groups</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {contactDetails.ContactGroups.map((group, index) => (
                <li key={index} className="text-gray-700">{group.Name} ({group.Status})</li>
              ))}
            </ul>
          </div>
        )}

        {contactDetails.ContactPersons && contactDetails.ContactPersons.length > 0 && (
          <div className="mt-5 pt-5 border-t">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Contact Persons</h3>
            {contactDetails.ContactPersons.map((person, index) => (
              <div key={index} className="mb-1 pb-1 border-b last:border-b-0 text-sm">
                <p className="text-gray-700"><strong className="text-gray-500">Name:</strong> {person.FirstName} {person.LastName}</p>
                <p className="text-gray-700"><strong className="text-gray-500">Email:</strong> {person.EmailAddress || 'N/A'}</p>
                <p className="text-gray-700"><strong className="text-gray-500">Include In Emails:</strong> {person.IncludeInEmails ? 'Yes' : 'No'}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-7 text-center">
          <Link
            to="/settings/xero-import-contacts"
            className="btn btn-secondary inline-flex items-center text-sm"
          >
            <FaArrowLeft className="mr-2" />
            Back to Import List
          </Link>
        </div>
      </div>
      <style jsx>{`
        .btn {
          padding: 0.5rem 1rem;
          font-weight: 600;
          border-radius: 0.375rem;
          transition: background-color 0.3s ease, opacity 0.3s ease;
          border: 1px solid transparent;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .btn-secondary {
          background-color: #E5E7EB;
          color: #1F2937;
          border: 1px solid #D1D5DB;
        }
        .btn-secondary:hover:not(:disabled) {
          background-color: #D1D5DB;
        }
      `}</style>
    </div>
  );
};

export default XeroContactDetailsPage;
